import { useMemo } from 'react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Select } from '@ankr.com/ui';
import { Box, Icon, MenuItem, Switch, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { useChainList } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import { TaskRoutesConfig } from 'modules/task';

import { useAppDispatch, useAppSelector } from '../../../store';
import { setGroupedTasks } from '../../../store/uiSlice';
import translation from './filters.translation';

export interface FilterValues {
  status: StatusFilter;
  networkValue: number;
  search: string;
}

interface Props {
  onChange: (values: Partial<FilterValues>) => void;
  status: number;
  search: string;
  networkValue: number;
}

export enum StatusFilter {
  Any,
  Active,
  Paused,
}

export const ANY_NETWORK_VALUE = 0;

export function Filters({
  onChange,
  search,
  status,
  networkValue,
}: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(translation);
  const dispatch = useAppDispatch();
  const { groupedTasks } = useAppSelector(state => state.ui);

  const navigate = useNavigate();

  const statusOptions = useMemo(
    () => [
      {
        label: t(keys.anyStatus),
        value: StatusFilter.Any,
      },
      {
        label: t(keys.active),
        value: StatusFilter.Active,
      },
      {
        label: t(keys.paused),
        value: StatusFilter.Paused,
      },
    ],
    [keys, t],
  );

  const chainList = useChainList();

  return (
    <div className={classes.root}>
      <div className={classes.selects}>
        <Select
          data-tid="dashboard-chain-select"
          variant="standard"
          className={classes.select}
          value={networkValue}
          onChange={event =>
            onChange({ networkValue: event.target.value as number })
          }
        >
          <MenuItem
            data-tid="dashboard-chain-select-item-any"
            key={ANY_NETWORK_VALUE}
            value={ANY_NETWORK_VALUE}
            className={cx(
              ANY_NETWORK_VALUE === networkValue && classes.selected,
            )}
          >
            <Typography variant="subtitle3" color="inherit">
              {t(keys.anyNetworks)}
            </Typography>
          </MenuItem>

          {chainList.map(({ chainId, name, icon }) => (
            <MenuItem
              data-tid={`dashboard-chain-select-item-${chainId}`}
              key={chainId}
              value={chainId}
              className={cx(chainId === networkValue && classes.selected)}
            >
              <div className={classes.networkItem}>
                {icon.rounded && (
                  <Box component="img" src={icon.rounded} width={24} mr={2} />
                )}

                <Typography variant="subtitle3" flex={1} color="inherit">
                  {name}
                </Typography>
              </div>
            </MenuItem>
          ))}
        </Select>

        <Select
          value={status}
          onChange={event =>
            onChange({ status: event.target.value as StatusFilter })
          }
          variant="standard"
          className={classes.select}
          data-tid="dashboard-status-select"
        >
          {statusOptions.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
              className={cx(option.value === status && classes.selected)}
              data-tid={`dashboard-status-select-item-${option.value}`}
            >
              <Typography variant="subtitle3" color="inherit">
                {option.label}
              </Typography>
            </MenuItem>
          ))}
        </Select>

        <div className={classes.switch}>
          <Switch
            data-tid="dashboard-grouping-select"
            checked={groupedTasks}
            onChange={(_, checked: boolean) =>
              dispatch(setGroupedTasks(checked))
            }
          />

          <Typography
            variant="subtitle3"
            color={theme => theme.palette.grey[600]}
            ml={2}
          >
            {t(keys.groupByContract)}
          </Typography>
        </div>
      </div>

      <div className={classes.searchAndButton}>
        <TextField
          data-tid="dashboard-create-search-input"
          variant="standard"
          classes={{ root: classes.textField }}
          placeholder={t(keys.search)}
          onChange={event => onChange({ search: event.target.value })}
          value={search}
          autoComplete="off"
          InputProps={{
            endAdornment: (
              <Icon>
                <Search className={classes.textFieldIcon} />
              </Icon>
            ),
          }}
        />

        <Button
          data-tid="dashboard-create-task-button"
          type="button"
          size="large"
          onClick={() => navigate(TaskRoutesConfig.createTask.generatePath())}
        >
          {t(keys.createTask)}
        </Button>
      </div>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flex: 1,

    [theme.breakpoints.up('md')]: {
      justifyContent: 'space-between',
    },

    [theme.breakpoints.down('md')]: {
      flexFlow: 'column nowrap',
      gap: theme.spacing(4),
    },

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(4),
    },
  },
  selects: {
    display: 'flex',
    gap: theme.spacing(4),
    flex: 1,

    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
    },

    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column nowrap',
    },
  },
  searchAndButton: {
    display: 'flex',
    gap: theme.spacing(6),

    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2),
    },

    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column nowrap',
      gap: theme.spacing(2),
    },
  },
  selectWrapper: {
    [theme.breakpoints.down('md')]: {
      flex: 1,
    },
  },
  select: {
    '&&&&': {
      flex: 1,
      marginTop: 0,
      width: '100%',

      [theme.breakpoints.up('md')]: {
        maxWidth: 205,
      },
    },
  },
  networkItem: {
    '&&': {
      display: 'flex',
      alignItems: 'center',
      color: 'inherit',
    },
  },
  textField: {
    '&&': {
      [theme.breakpoints.up('md')]: {
        width: '180px',
      },

      [theme.breakpoints.down('md')]: {
        flex: 1,
      },
    },

    '&& > div': {
      [theme.breakpoints.up('md')]: {
        minWidth: '180px',
      },
    },
  },
  textFieldIcon: {
    color: theme.palette.grey[500],
  },
  switch: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    marginRight: theme.spacing(6),

    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(0),
    },

    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(4, 0, 0, 0),
    },
  },
  selected: {
    '&&': {
      color: theme.palette.primary.main,
    },
  },
}));
